import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import businessOnboardingApi from '../../api/businessOnboarding';

/**
 * Business Entry Point Component
 * 
 * Routes users based on their business ownership status:
 * - No businesses → Start onboarding
 * - Exactly 1 incomplete business → Resume onboarding
 * - Otherwise → Go to admin panel
 */
export default function BusinessEntry() {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkBusinessStatus = async () => {
      try {
        console.log('[BusinessEntry] Checking business status for user:', userId);
        
        if (!userId) {
          console.log('[BusinessEntry] No user ID from AuthContext, redirecting to login');
          navigate('/login');
          return;
        }

        // Get user's businesses
        console.log('[BusinessEntry] Fetching businesses from API...');
        const response = await businessOnboardingApi.getUserBusinesses(userId);
        console.log('[BusinessEntry] Full API response:', response);
        
        // Handle response structure (response.data from axios contains the actual response)
        const businesses = response.data?.businesses || response.businesses || [];
        console.log('[BusinessEntry] Found businesses:', businesses);

        if (businesses.length === 0) {
          // No businesses → Start onboarding
          console.log('[BusinessEntry] No businesses found, starting onboarding');
          navigate('/business/onboarding/new');
        } else {
          // Only auto-resume onboarding if the user's one and only business is incomplete.
          // If they have multiple businesses, never force onboarding; let them manage/select.
          if (businesses.length === 1 && businesses[0]?.onboarding_completed === false) {
            console.log('[BusinessEntry] Single incomplete business found, resuming onboarding:', businesses[0].id);
            navigate(`/business/onboarding/${businesses[0].id}`);
            return;
          }

          // Otherwise go to admin panel. Prefer a completed business if available.
          const preferred = businesses.find((b) => b.onboarding_completed === true) || businesses[0];
          console.log('[BusinessEntry] Going to admin panel. Preferred business:', preferred?.id);
          navigate(`/business-admin?business_id=${preferred?.id}`);
        }
      } catch (err) {
        console.error('[BusinessEntry] Error checking business status:', err);
        console.error('[BusinessEntry] Error details:', err.response?.data);
        setError('Failed to load your business information');
        setLoading(false);
      }
    };

    if (userId) {
      checkBusinessStatus();
    }
  }, [navigate, userId]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <CircularProgress size={40} sx={{ color: '#3b82f6' }} />
        <p className="mt-4 text-gray-600">Loading your business information...</p>
      </div>
    </div>
  );
}
